import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { CalendarIcon, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Area,
  AreaChart,
  ComposedChart,
  ReferenceLine,
} from 'recharts';
import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react';
import { fetchPulseDaily, fetchPulseByBand, fetchOrgAnchorDaily, type BandCellRow, type AnchorDailyRow } from '@/lib/admin/admin-api';
import { SuppressedCell } from '@/components/admin/SuppressedCell';

type Dim = 'department' | 'location' | 'level' | 'tenure_years' | 'team';
type Preset = '7d' | '30d' | '90d' | '1y' | 'all' | 'custom';

function daysAgoISO(n: number) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

const fmtDay = (s: string) => {
  const d = new Date(s + 'T00:00:00Z');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const SERIES_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--accent))',
  'hsl(var(--chart-1, var(--primary)))',
  'hsl(var(--chart-2, var(--accent)))',
  'hsl(var(--chart-3, var(--primary)))',
  'hsl(var(--chart-4, var(--accent)))',
  'hsl(var(--chart-5, var(--primary)))',
];

const PX_PER_POINT = 56; // horizontal density per day
const SCROLL_THRESHOLD = 14; // start scrolling beyond ~2 weeks of points

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover/95 backdrop-blur px-3 py-2 shadow-lg text-xs">
      <div className="font-medium text-foreground mb-1">{label}</div>
      <ul className="space-y-0.5">
        {payload.map((p: any) => (
          <li key={p.dataKey} className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color }} />
            <span className="text-muted-foreground">{p.name}</span>
            <span className="ml-auto tabular-nums text-foreground">
              {Number(p.value).toFixed(2)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ScrollableChart({
  pointCount,
  height,
  children,
}: {
  pointCount: number;
  height: number;
  children: React.ReactElement;
}) {
  const scroll = pointCount > SCROLL_THRESHOLD;
  const minWidth = scroll ? pointCount * PX_PER_POINT : undefined;
  return (
    <div className={cn('w-full', scroll && 'overflow-x-auto')} style={{ height }}>
      <div style={{ minWidth, height: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DateRangeFilter({
  preset,
  setPreset,
  customFrom,
  customTo,
  setCustom,
}: {
  preset: Preset;
  setPreset: (p: Preset) => void;
  customFrom?: Date;
  customTo?: Date;
  setCustom: (r: { from?: Date; to?: Date }) => void;
}) {
  const label =
    preset === 'custom' && customFrom && customTo
      ? `${format(customFrom, 'MMM d')} – ${format(customTo, 'MMM d, yyyy')}`
      : 'Custom range';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ToggleGroup
        type="single"
        value={preset === 'custom' ? '' : preset}
        onValueChange={(v) => v && setPreset(v as Preset)}
        size="sm"
        variant="outline"
      >
        <ToggleGroupItem value="7d">7d</ToggleGroupItem>
        <ToggleGroupItem value="30d">30d</ToggleGroupItem>
        <ToggleGroupItem value="90d">90d</ToggleGroupItem>
        <ToggleGroupItem value="1y">1y</ToggleGroupItem>
        <ToggleGroupItem value="all">All</ToggleGroupItem>
      </ToggleGroup>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={preset === 'custom' ? 'default' : 'outline'}
            size="sm"
            className="gap-2"
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="range"
            selected={{ from: customFrom, to: customTo }}
            onSelect={(r: any) => {
              setCustom({ from: r?.from, to: r?.to });
              if (r?.from && r?.to) setPreset('custom');
            }}
            numberOfMonths={2}
            initialFocus
            className={cn('p-3 pointer-events-auto')}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function Trends() {
  const { orgId } = useOutletContext<{ orgId: string }>();
  const [dim, setDim] = useState<Dim>('department');
  const [preset, setPreset] = useState<Preset>('30d');
  const [custom, setCustom] = useState<{ from?: Date; to?: Date }>({});

  const { from, to } = useMemo(() => {
    if (preset === 'custom' && custom.from && custom.to) {
      return {
        from: custom.from.toISOString().slice(0, 10),
        to: custom.to.toISOString().slice(0, 10),
      };
    }
    const days = preset === '7d' ? 7 : preset === '90d' ? 90 : preset === '1y' ? 365 : preset === 'all' ? 3650 : 30;
    return { from: daysAgoISO(days), to: daysAgoISO(-1) };
  }, [preset, custom]);

  const daily = useQuery({
    queryKey: ['admin', 'daily', orgId],
    queryFn: () => fetchPulseDaily(orgId),
  });
  const band = useQuery({
    queryKey: ['admin', 'band', orgId, dim, from, to],
    queryFn: () => fetchPulseByBand(orgId, dim, from, to),
  });
  const anchorDaily = useQuery({
    queryKey: ['admin', 'anchor_daily', orgId, from, to],
    queryFn: () => fetchOrgAnchorDaily(orgId, from, to),
  });

  const anchorPanels = useMemo(() => {
    const rows: AnchorDailyRow[] = anchorDaily.data ?? [];
    const byAnchor = new Map<string, { day: string; value: number }[]>();
    for (const r of rows) {
      if (!byAnchor.has(r.anchor_name)) byAnchor.set(r.anchor_name, []);
      byAnchor.get(r.anchor_name)!.push({
        day: r.day,
        value: Number(Number(r.mean_value).toFixed(2)),
      });
    }
    return Array.from(byAnchor.entries())
      .map(([name, points]) => {
        const sorted = points.slice().sort((a, b) => a.day.localeCompare(b.day));
        const first = sorted[0]?.value ?? null;
        const last = sorted[sorted.length - 1]?.value ?? null;
        const delta = first != null && last != null ? Number((last - first).toFixed(2)) : null;
        const values = sorted.map((p) => p.value);
        const min = values.length ? Math.min(...values) : null;
        const max = values.length ? Math.max(...values) : null;
        const avg = values.length ? Number((values.reduce((s, v) => s + v, 0) / values.length).toFixed(2)) : null;
        return {
          name,
          data: sorted.map((p) => ({ ...p, label: fmtDay(p.day) })),
          first,
          last,
          delta,
          min,
          max,
          avg,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [anchorDaily.data]);


  const dailyData = useMemo(() => {
    return (daily.data ?? [])
      .slice()
      .filter((r) => r.day >= from && r.day <= to)
      .sort((a, b) => a.day.localeCompare(b.day))
      .map((r) => ({
        day: fmtDay(r.day),
        avg_pulse: Number(Number(r.avg_pulse).toFixed(2)),
        n: r.n_employees,
      }));
  }, [daily.data, from, to]);

  const { bandSeries, bandData, suppressed } = useMemo(() => {
    const rows: BandCellRow[] = band.data ?? [];
    const visible = rows.filter((r) => r.band_id !== null && r.day);
    const labels = Array.from(new Set(visible.map((r) => r.band_label ?? 'Unknown')));
    const byDay = new Map<string, Record<string, number | string>>();
    for (const r of visible) {
      const key = r.day!;
      if (!byDay.has(key)) byDay.set(key, { day: key });
      byDay.get(key)![r.band_label ?? 'Unknown'] = Number(Number(r.avg_pulse ?? 0).toFixed(2));
    }
    const data = Array.from(byDay.values())
      .sort((a, b) => String(a.day).localeCompare(String(b.day)))
      .map((d) => ({ ...d, day: fmtDay(String(d.day)) }));
    return { bandSeries: labels, bandData: data, suppressed: rows[0]?.suppressed_cell_count ?? 0 };
  }, [band.data]);

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-semibold">Trends</h2>
          <p className="text-sm text-muted-foreground">
            Days and cells with fewer than 5 employees are omitted entirely (R3).
          </p>
        </div>
        <DateRangeFilter
          preset={preset}
          setPreset={setPreset}
          customFrom={custom.from}
          customTo={custom.to}
          setCustom={setCustom}
        />
      </div>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="text-base font-heading">Org pulse — daily</CardTitle>
          <CardDescription>
            All visible days have n ≥ 5. Scroll horizontally as history grows.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {daily.isLoading ? (
            <div className="h-72 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : daily.error ? (
            <p className="text-sm text-destructive">{(daily.error as Error).message}</p>
          ) : dailyData.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No days meet the k ≥ 5 floor in this range.
            </p>
          ) : (
            <ScrollableChart pointCount={dailyData.length} height={288}>
              <ComposedChart data={dailyData} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="pulseFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="day"
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis
                  domain={[1, 10]}
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={32}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="avg_pulse"
                  stroke="none"
                  fill="url(#pulseFill)"
                  isAnimationActive
                />
                <Line
                  type="monotone"
                  dataKey="avg_pulse"
                  name="Avg pulse"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.5}
                  dot={{ r: 3, strokeWidth: 0, fill: 'hsl(var(--primary))' }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ScrollableChart>
          )}
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="text-base font-heading">By band</CardTitle>
          <CardDescription>Slice by department, location, level, tenure, or team across the selected range.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={dim} onValueChange={(v) => setDim(v as Dim)} className="mb-4">
            <TabsList>
              <TabsTrigger value="department">Department</TabsTrigger>
              <TabsTrigger value="location">Location</TabsTrigger>
              <TabsTrigger value="level">Level</TabsTrigger>
              <TabsTrigger value="tenure_years">Tenure</TabsTrigger>
              <TabsTrigger value="team">Team</TabsTrigger>
            </TabsList>
          </Tabs>

          {band.isLoading ? (
            <div className="h-72 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : band.error ? (
            <p className="text-sm text-destructive">{(band.error as Error).message}</p>
          ) : bandSeries.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No cells meet the k ≥ 5 floor for this dimension in this range.
            </p>
          ) : (
            <>
              <ScrollableChart pointCount={bandData.length} height={320}>
                <LineChart data={bandData} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis
                    domain={[1, 10]}
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={32}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  {bandSeries.map((label, i) => (
                    <Line
                      key={label}
                      type="monotone"
                      dataKey={label}
                      name={label}
                      stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 2.5, strokeWidth: 0 }}
                      activeDot={{ r: 4.5 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ScrollableChart>
              <div className="mt-3">
                <SuppressedCell count={Number(suppressed) || 0} />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="text-base font-heading">Anchor history</CardTitle>
          <CardDescription>
            One panel per anchor — daily org mean on a 1–10 scale. Days with fewer than 5 respondents are omitted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {anchorDaily.isLoading ? (
            <div className="h-72 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : anchorDaily.error ? (
            <p className="text-sm text-destructive">{(anchorDaily.error as Error).message}</p>
          ) : anchorPanels.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No anchor responses meet the k ≥ 5 floor in this range.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {anchorPanels.map((panel, i) => {
                const color = SERIES_COLORS[i % SERIES_COLORS.length];
                const trendIcon =
                  panel.delta == null || Math.abs(panel.delta) < 0.05 ? (
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : panel.delta > 0 ? (
                    <ArrowUp className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-destructive" />
                  );
                const gradId = `anchor-grad-${i}`;
                return (
                  <div
                    key={panel.name}
                    className="rounded-lg border border-border bg-card/40 p-3 flex flex-col gap-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate" title={panel.name}>
                          {panel.name}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          avg {panel.avg ?? '—'} · range {panel.min ?? '—'}–{panel.max ?? '—'}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="flex items-center justify-end gap-1 text-lg font-semibold tabular-nums">
                          <span style={{ color }}>{panel.last ?? '—'}</span>
                        </div>
                        <div className="flex items-center justify-end gap-1 text-[11px] text-muted-foreground tabular-nums">
                          {trendIcon}
                          <span>
                            {panel.delta == null
                              ? 'no change'
                              : panel.delta > 0
                                ? `+${panel.delta}`
                                : `${panel.delta}`}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="h-20">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={panel.data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                              <stop offset="100%" stopColor={color} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <YAxis domain={[1, 5]} hide />
                          <XAxis dataKey="label" hide />
                          <ReferenceLine y={3} stroke="hsl(var(--border))" strokeDasharray="2 3" />
                          <Tooltip
                            cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeOpacity: 0.3 }}
                            content={({ active, payload }: any) => {
                              if (!active || !payload?.length) return null;
                              const p = payload[0];
                              return (
                                <div className="rounded-md border border-border bg-popover/95 backdrop-blur px-2 py-1.5 shadow-md text-xs">
                                  <div className="text-muted-foreground">{p.payload.label}</div>
                                  <div className="font-medium tabular-nums" style={{ color }}>
                                    {Number(p.value).toFixed(2)}
                                  </div>
                                </div>
                              );
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke={color}
                            strokeWidth={2}
                            fill={`url(#${gradId})`}
                            dot={false}
                            activeDot={{ r: 3 }}
                            isAnimationActive={false}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
